/**
 * Base URL Configuration
 * Centralized configuration for all environment base URLs.
 * 
 * To change the environment, update the ENVIRONMENT constant below.
 * All other files will automatically use the correct URLs.
 */

/**
 * Available environments
 */
export type Environment = 'localhost' | 'dev' | 'uat' | 'prod';

/**
 * Current environment
 * Change this to switch between environments
 * 
 * Options:
 * - 'localhost' - Local development (dream2.test)
 * - 'dev' - Development server (dev-dream.iodt.my)
 * - 'uat' - UAT server (uat-dream.iodt.my)
 * - 'prod' - Production server (dream.iodt.my)
 */
const ENVIRONMENT: Environment = (import.meta.env.VITE_ENVIRONMENT as Environment) || 'localhost';

/**
 * Base URL configuration for each environment
 */
const BASE_URLS = {
  localhost: {
    // Use /api so Vite proxy forwards to backend (avoids CORS when frontend on localhost:3000)
    api: import.meta.env.DEV ? '/api' : 'http://dream2.test/api',
    app: 'http://dream2.test', // Laravel backend app
    frontend: 'http://localhost:3000', // Vite dev server (React app)
  },
  dev: {
    api: 'https://dream-dev.iodt.my/api',
    app: 'https://dream-dev.iodt.my',
    frontend: 'https://dream-dev.iodt.my',
  },
  uat: {
    api: 'https://dream-uat.iodt.my/api',
    app: 'https://dream-uat.iodt.my',
    frontend: 'https://dream-uat.iodt.my',
  },
  prod: {
    api: 'https://dream.iodt.my/api',
    app: 'https://dream.iodt.my',
    frontend: 'https://dream.iodt.my',
  },
} as const;

/**
 * Get base URLs for the current environment
 */
export const getBaseUrls = () => {
  return BASE_URLS[ENVIRONMENT];
};

/**
 * Get API base URL
 */
export const getApiBaseUrl = (): string => {
  // Allow override via environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  return getBaseUrls().api;
};

/**
 * Get application base URL
 */
export const getAppBaseUrl = (): string => {
  // Allow override via environment variable
  if (import.meta.env.VITE_APP_URL) {
    return import.meta.env.VITE_APP_URL;
  }
  return getBaseUrls().app;
};

/**
 * Get frontend URL (for redirects, links, etc.)
 */
export const getFrontendUrl = (): string => {
  // Allow override via environment variable
  if (import.meta.env.VITE_FRONTEND_URL) {
    return import.meta.env.VITE_FRONTEND_URL;
  }
  return getBaseUrls().frontend;
};

/**
 * Get current environment
 */
export const getEnvironment = (): Environment => {
  return ENVIRONMENT;
};

/**
 * Check if running in localhost environment
 */
export const isLocalhost = (): boolean => {
  return ENVIRONMENT === 'localhost';
};

/**
 * Check if running in development environment
 */
export const isDev = (): boolean => {
  return ENVIRONMENT === 'dev';
};

/**
 * Check if running in UAT environment
 */
export const isUat = (): boolean => {
  return ENVIRONMENT === 'uat';
};

/**
 * Check if running in production environment
 */
export const isProd = (): boolean => {
  return ENVIRONMENT === 'prod';
};

/**
 * Export base URLs for direct access
 */
export const baseUrls = getBaseUrls();
